import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';

import { distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { AspectRatios, IconPosition } from '@fcom/ui-components';
import { FinnairOrder, FinnairTravelEndpoint } from '@fcom/dapi/api/models';
import { MediaQueryService } from '@fcom/common/services';
import { CmsArticle, CmsImageData } from '@fcom/core-api/interfaces';
import { CmsDataService } from '@fcom/core/index';
import { LanguageService } from '@fcom/ui-translate/index';
import { ExpediaHotelOrigin, buildExpediaHotelDeeplink, removeTimezone } from '@fcom/common/utils';
import { TzDate } from '@fcom/core';

@Component({
  selector: 'fin-mmb-hotels-deeplink-in-contentbox',
  templateUrl: './hotels-deeplink-in-contentbox.component.html',
  styleUrls: ['./hotels-deeplink-in-contentbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MmbHotelsDeeplinkInContentBoxComponent implements OnInit {
  @Input({ required: true }) order$: Observable<FinnairOrder>;

  @Input({ required: true }) isWithin24HoursOfCreation: boolean;

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconLibrary = IconLibrary;
  readonly CustomIcon = CustomIcon;
  readonly IconPosition = IconPosition;

  isSupportedLanguage = false;
  hotelsImage$: Observable<CmsImageData>;
  aspectRatio$: Observable<keyof typeof AspectRatios>;
  cityName$: Observable<string>;
  deeplink$: Observable<string>;
  legislationModalOpen = signal(false);

  constructor(
    private mediaQueryService: MediaQueryService,
    private changeDetectorRef: ChangeDetectorRef,
    private cmsDataService: CmsDataService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.hotelsImage$ = this.languageService.translate('MMB.fragments.expediaHotelsImage.url').pipe(
      switchMap((url) => {
        return this.cmsDataService.getFragmentJson(url).pipe(
          map((fragment: CmsArticle) => {
            return fragment.picture;
          })
        );
      })
    );

    this.aspectRatio$ = this.mediaQueryService.isBreakpoint$('mobile').pipe(
      distinctUntilChanged(),
      map((isMobile) => {
        return isMobile ? '16x9' : '21x9';
      })
    );

    this.cityName$ = this.order$.pipe(
      map((order) => {
        const locationCode = order.bounds[0].arrival.locationCode;

        return order.locations[locationCode].cityName || locationCode;
      })
    );

    this.deeplink$ = this.order$.pipe(map((order) => this.buildExpediaHotelDeeplink(order)));
  }

  openLegislationModal(event: Event): void {
    if (!this.isWithin24HoursOfCreation) {
      return;
    }

    event.preventDefault();

    this.legislationModalOpen.set(true);
    this.changeDetectorRef.detectChanges();
  }

  private buildExpediaHotelDeeplink(order: FinnairOrder): string {
    const arriving = order.bounds[0].arrival;
    const leaving = order.bounds.length > 1 ? order.bounds[order.bounds.length - 1]?.departure : null;
    const actualLeaving = leaving || this.calculateDefaultDeparture(arriving);

    return buildExpediaHotelDeeplink(
      order,
      arriving,
      actualLeaving,
      this.languageService.langValue,
      ExpediaHotelOrigin.TRIP_OVERVIEW
    );
  }

  private calculateDefaultDeparture(arriving: FinnairTravelEndpoint): FinnairTravelEndpoint {
    return {
      ...arriving,
      dateTime: new TzDate(removeTimezone(arriving.dateTime))
        .addHours(24 * 3) // Default 3 days stay
        .toISOString(),
    };
  }
}
